import React, { useEffect, useState, useCallback } from "react";
import axios from "../../utils/axiosConfig";
import "../styles/AdminDownloadManager.css";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaFileAlt,
} from "react-icons/fa";

const API = "/api/downloads";

/* FILE ICON */
const getFileIcon = (type) => {
  if (!type) return <FaFileAlt />;

  if (type.includes("pdf")) return <FaFilePdf className="pdf" />;
  if (type.includes("word")) return <FaFileWord className="doc" />;
  if (type.includes("excel")) return <FaFileExcel className="xls" />;
  if (type.includes("zip")) return <FaFileArchive className="zip" />;

  return <FaFileAlt />;
};

/* PREVIEW CHECK */
const isPreviewable = (file) => {
  if (!file) return false;
  return file.type.includes("pdf") || file.type.includes("image");
};

const AdminDownloadManager = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    description: "",
    category: "",
    active: 1,
  });

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [progress, setProgress] = useState(0);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(1);
  const perPage = 5;

  /* RESET */
  const resetForm = () => {
    setForm({ description: "", category: "", active: 1 });
    setFile(null);
    setPreview("");
    setProgress(0);
    setEditId(null);
  };

  /* ✅ MOVE THIS ABOVE useEffect */
  const closeModal = useCallback(() => {
    resetForm();
    setShowModal(false);
  }, []);

  /* FETCH */
  const fetchData = async () => {
    const res = await axios.get(API);
    setData(res.data);
    setFiltered(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ESC CLOSE */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [closeModal]);

  /* SEARCH */
  useEffect(() => {
    const result = data.filter((item) =>
      item.description.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(result);
    setPage(1);
  }, [search, data]);

  /* FILE */
  const handleFile = (f) => {
    setFile(f);
    if (f && isPreviewable(f)) {
      setPreview(URL.createObjectURL(f));
    } else {
      setPreview("");
    }
  };

  /* DRAG DROP */
  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  /* SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("description", form.description);
    fd.append("category", form.category);
    fd.append("active", form.active);
  
    try {
      await axios({
        method: editId ? "put" : "post",
        url: editId ? `${API}/${editId}` : API,
        data: fd,
        onUploadProgress: (p) => {
          setProgress(Math.round((p.loaded * 100) / p.total));
        },
      });
  
      fetchData();
      closeModal();
  
    } catch (err) {
  
      /* ✅ PUT YOUR CODE HERE */
      let msg = "Upload failed";
  
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        msg = err.response.data.message;
      }
  
      alert(msg);
    }
  };

  /* EDIT */
  const handleEdit = (item) => {
    setForm(item);
    setEditId(item.id);
    setShowModal(true);
  };

  /* DELETE */
  const handleDelete = async (id) => {
    if (window.confirm("Delete this file?")) {
      await axios.delete(`${API}/${id}`);
      fetchData();
    }
  };

  /* TOGGLE */
  const toggleStatus = async (item) => {
    await axios.patch(`${API}/toggle/${item.id}`, {
      active: item.active ? 0 : 1,
    });
    fetchData();
  };

  /* PAGINATION */
  const start = (page - 1) * perPage;
  const currentData = filtered.slice(start, start + perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  return (
    <div className="admin-download">

      <div className="admin-header">
        <h2>📁 Download Manager</h2>

        <input
          type="text"
          placeholder="Search files..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button className="btn primary" onClick={() => setShowModal(true)}>
          + Add File
        </button>
      </div>

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Description</th>
              <th>Category</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {currentData.map((item) => (
              <tr key={item.id}>
                <td className="file-cell">
                  {getFileIcon(item.filetype)}
                </td>
                <td>{item.description}</td>
                <td>{item.category}</td>
                <td>{item.created_at}</td>

                <td>
                  <span
                    className={`status-pill ${item.active ? "on" : "off"}`}
                    onClick={() => toggleStatus(item)}
                  >
                    {item.active ? "Active" : "Inactive"}
                  </span>
                </td>

                <td>
                  <button className="btn edit" onClick={() => handleEdit(item)}>Edit</button>
                  <button className="btn delete" onClick={() => handleDelete(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        {Array.from({ length: totalPages }, (_, i) => (
          <button key={i} onClick={() => setPage(i + 1)}>
            {i + 1}
          </button>
        ))}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div
            className="modal-box"
            onClick={(e) => e.stopPropagation()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >

            <button className="close-btn" onClick={closeModal}>×</button>

            <h3>{editId ? "Edit File" : "Upload File"}</h3>

            <form onSubmit={handleSubmit} className="form-grid">

              <input
                type="text"
                placeholder="Description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                required
              />

              <input
                type="text"
                placeholder="Category"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
              />

              <div className="drop-zone">
                <p>Drag & Drop file here or click</p>
                <input type="file" onChange={(e) => handleFile(e.target.files[0])} />
              </div>

              {file && (
                <div className="file-info-box">
                  <div className="file-info-left">
                    {getFileIcon(file.type)}
                  </div>

                  <div className="file-info-right">
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                    <span className="file-badge">
                      {file.type && file.type.split("/")[1]
                        ? file.type.split("/")[1].toUpperCase()
                        : ""}
                    </span>
                  </div>
                </div>
              )}

              {preview && (
                <iframe src={preview} title="preview"></iframe>
              )}

              {progress > 0 && (
                <div className="progress-bar">
                  <div style={{ width: `${progress}%` }}></div>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn primary">
                  {editId ? "Update" : "Upload"}
                </button>

                <button type="button" className="btn secondary" onClick={closeModal}>
                  Reset
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDownloadManager;